import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import dayjs from 'dayjs';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import SafeUrlAssembler from 'safe-url-assembler';
import { ApiEndpointService } from '../../core/services/api-endpoint.service';
import { Feature } from '../../shared/models/feature.enum';
import { IDDCredit } from '../../ship/models/dd-credit.interface';
import { IDDSubscriptionType } from '../../ship/models/dd-subscription-type.interface';
import { IDDSubscription } from '../../ship/models/dd-subscription.interface';
import { IAddressByZipCode } from '../dd-address-confidence-tool/interfaces/addressByZipCode.interface';
import { IPostScoreAddress } from '../dd-address-confidence-tool/interfaces/postScoreAddress.interface';
import { IPostScoreAddressResponse } from '../dd-address-confidence-tool/interfaces/postScoreAddressResponse.interface';
import { CancelSubscriptionPopupComponent } from '../dd-manage-subscription/cancel-subscription-popup/cancel-subscription-popup.component';
import { IScoreHistory } from '../dd-score-history/scoreHistory.interface';
import { DeliveryDefenseSubscriptionRenewDialogComponent } from '../delivery-defense-subscription-renew-dialog/delivery-defense-subscription-renew-dialog.component';
import { IScoreHistoryTableLookup } from "../dd-score-history/dd-score-history-table/scoreHistoryTableLookup.interface";
import { IPostAddressUpload } from '../dd-address-confidence-tool/interfaces/postAddressUpload.interface';
import { IPostAddressUploadResponse } from '../dd-address-confidence-tool/interfaces/postAddressUploadResponse.interface';
import { FormGroup } from '@angular/forms';
import { IGetAddressUploadInterface } from '../dd-address-confidence-tool/interfaces/IGetAddressUpload.interface';
import { IPostRecentUploadsInterface } from '../dd-address-confidence-tool/interfaces/IPostRecentUploads.interface';
import { IPostAddressReview } from '../dd-address-confidence-tool/interfaces/postAddressReview.interface';
import { IPostAddressReviewResponse } from '../dd-address-confidence-tool/interfaces/postAddressReviewResponse.interface';
import { IReviewRecentUploadInterface } from '../dd-address-confidence-tool/interfaces/IReviewRecentUpload.interface';
import { IPostAddressUploadDraftResponse } from '../dd-address-confidence-tool/interfaces/postAddressUploadDraftResponse.interface';
import { IPutAddressUploadDraft } from '../dd-address-confidence-tool/interfaces/putAddressUploadDraft.interface';

@Injectable({
    providedIn: 'root',
})
export class DeliveryDefenseService {
    public deliveryDefenseCreditsUpdated$ = new Subject<IDDCredit>();
    public offerDialogClosed$ = new Subject();

    public advancedFilterScoreChanged$ = new Subject<{ min: number, max: number }>();
    public advancedFilterScoreChangedRecentUpload$ = new Subject<{ min: number, max: number }>();
    public advancedFilterClosed$ = new Subject<{ closed: boolean }>();

    public addressConfidenceToolSearched$ = new Subject<{ searched: boolean }>();

    // Below subjects are all used for Address Uploads section (which is inside Address Confidence Tool)
    public editAddressData$ = new Subject<{ data: FormGroup[], isDeleteAllErrorRecords: boolean }>();   // Used to listen to modifications of editAddressFormGroup in dd-popups-component and use it in dd-upload-address-file-review-errors.component
    public submitForScoring$ = new Subject<{ data: [], isSubmitForScoring: boolean }>();
    public cancelSubmission$ = new Subject<{ isCancelSubmissionClicked: boolean }>();
    public reviewAddressDetails$ = new Subject<{ isShowReviewAddressDetails: boolean }>();
    public submissionComplete$ = new Subject<{ isSubmissionComplete: boolean }>();
    public fileName$ = new BehaviorSubject<{ fileName: string }>(null);  // Reason why this is BehaviorSubject is bc it listens immediately, whereas the regular Subject waits until the component is initialized first (so imagine we had a popup)
    public saveProgress$ = new Subject<{ isSaveProgress: boolean }>();
    
    // Subject variables to listen to recent upload review page
    public reviewingRecentUpload$ = new Subject<{ data: IReviewRecentUploadInterface }>();
    public reviewRecentUploadAddressDetails$ = new Subject<{ isShowReviewAddressDetails: boolean }>();
    public editRecentUploadData$ = new Subject<{ data: FormGroup[], isDeleteAllErrorRecords: boolean }>(); 
    public cancelRecentUploadSubmission$ = new BehaviorSubject<{ isCancelSubmissionClicked: boolean }>(null);
    public submitRecentUploadForScoring$ = new BehaviorSubject<{ data: [], isSubmitForScoring: boolean, fileName: string, batchId: string }>(null);
    public submissionRecentUploadComplete$ = new Subject<{ isSubmissionComplete: boolean }>();

    
    constructor(private httpClient: HttpClient,
                private apiEndpointService: ApiEndpointService,
                private readonly dialog: MatDialog,
    ) {
    }

    public getAgreementBeta(productId: Feature): Observable<string> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getAgreementBeta'))
            .param('productId', productId)
            .toString();
        return this.httpClient.get<string>(endpoint);
    }

    public acceptAgreementBeta(productId: Feature): Observable<void> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('acceptDDAgreementBeta'))
            .toString();
        const data = { ProductID: productId };
        return this.httpClient.post<void>(endpoint, data);
    }

    public declineAgreementBeta(): Observable<void> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('declineDDAgreementBeta'))
            .toString();
        return this.httpClient.delete<void>(endpoint);
    }

    public turnOffDeliveryDefense(): Observable<void> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('turnOffDeliveryDefense'))
            .toString();
        return this.httpClient.put<void>(endpoint, {});
    }

    public subscribeToDeliveryDefense(): Observable<void> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('subscribeToDeliveryDefense'))
            .toString();
        const data = {
            SubStartDate: dayjs().format('YYYY-MM-DD'),
        };

        return this.httpClient.post<void>(endpoint, data);
    }

    public feedbackDeliveryDefense(feedback: any): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('ddFeedback'))
            .toString();
        return this.httpClient.post<any>(endpoint, feedback);
    }

    public getSubscriberDetails(): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('subscriptionDetails')).toString();
        return this.httpClient.get<any>(endpoint);
    }

    public startSubscribe(): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('subscribeToDeliveryDefense'))
            .toString();
        return this.httpClient.post<any>(endpoint, {});
    }

    public getRemainingDDCredit(): Observable<IDDCredit> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.endpointUrls.getRemainingDDCredits)
            .toString();
        return this.httpClient.get<IDDCredit>(endpoint);
    }

    public getCustomerSubscription(): Observable<IDDSubscription> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getDDCustomerSubscription'))
            .toString();
        return this.httpClient.get<IDDSubscription>(endpoint);
    }

    public getSubscriptionType(subscriptionTypeNumber: number): Observable<IDDSubscriptionType> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getDDSubscriptionType'))
            .param('subscriptionType', subscriptionTypeNumber)
            .toString();
        return this.httpClient.get<IDDSubscriptionType>(endpoint)
                   .pipe(
                       map((subscriptionType) => {
                           subscriptionType.ShipmentCount
                               = +(subscriptionType?.SubDescription?.split(' ')?.[0] || 0);
                           subscriptionType.HighVolumeProgramNumber
                               = +(subscriptionType?.SubType?.split(' ')?.[subscriptionType?.SubType?.length || 0]
                               || (subscriptionTypeNumber - 1)
                               || 0);

                           return subscriptionType;
                       }),
                   );
    }

    public requestMoreScore(data: any): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('requestMoreScores'))
            .toString();
        return this.httpClient.post<any>(endpoint, data);
    }

    public handleZeroCredit(ddCredit: IDDCredit): void {
        if (!ddCredit || ddCredit?.ScoresRemaining > 0) {
            return;
        }

        let dialogType: 'complimentary' | 'regular' | 'form' | 'upgrade' | 'downgrade';

        switch (ddCredit?.SubTypeID) {
            case 1: // ran out of credits.
                // [MV3-6597] Show `Need More Scores` dialog instead of the paid subscription dialog.
                dialogType = 'form';
                break;
            case 5: // ran out of 50 complimentary credits.
                dialogType = 'complimentary';
                break;
            default:
                throw new Error(`Unsupported SubTypeID: ${ ddCredit.SubTypeID }. Only the SubTypeID of 1 and 4 are supported for DD Score dialog.`);
        }

        this.showOfferDialog(dialogType);
    }

    public handleSubscriptionTierChanged(oldSubscriptionTier: number, newSubscriptionTier: number): void {
        const isNoChange =
            !oldSubscriptionTier
            || !newSubscriptionTier
            || oldSubscriptionTier === newSubscriptionTier;
        if (isNoChange) {
            return;
        }

        const isTrailEnded = oldSubscriptionTier === 5 && newSubscriptionTier === 1;
        if (isTrailEnded) {
            return;
        }

        const isDowngraded = oldSubscriptionTier > newSubscriptionTier && oldSubscriptionTier !== 5;
        if (isDowngraded) {
            this.handleTierDowngraded(oldSubscriptionTier, newSubscriptionTier);
            return;
        }

        const isUpgraded = oldSubscriptionTier < newSubscriptionTier
            || (oldSubscriptionTier === 5 && newSubscriptionTier > 1);
        if (isUpgraded) {
            this.handleTierUpgraded(newSubscriptionTier);
            return;
        }

        throw new Error('No upgrade/downgrade condition matched');
    }

    public showSubscriptionCancellationDialog(): void {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '966px',
            data: {
                editMode: false,
            },
        };

        const cancelDialogRef = this.dialog.open(CancelSubscriptionPopupComponent, dialogConfig);
        cancelDialogRef.afterClosed().subscribe((result) => {
        });
    }

    public showOfferDialog(dialogType: 'complimentary' | 'regular' | 'form' | 'upgrade' | 'downgrade',
                           data?: any,
    ): void {
        const dialogConfig: MatDialogConfig = {
            disableClose: true,
            width: '39.875rem',
            data: {
                mode: dialogType,
            },
        };

        Object.assign(dialogConfig.data, data);

        const dialogRef = this.dialog.open(DeliveryDefenseSubscriptionRenewDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe((result) => {
            this.offerDialogClosed$.next(null);
        });
    }

    public deleteSubscription(data: any): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('subscribeToDeliveryDefense'))
            .toString();
        return this.httpClient.delete<any>(endpoint, { body: data });
    }

    public getScoreHistory(data: IScoreHistoryTableLookup): Observable<IScoreHistory[]> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('scoreHistory'))
            .query({
                page: data.pageNumber,
                per_page: data.pageSize,
                scoreFrom: data.scoreFrom,
                scoreTo: data.scoreTo,
                street: data?.street,
                city: data?.city,
                state: data?.state,
                zip: data?.zipCode,
            })
            .toString();
        return this.httpClient.get<IScoreHistory[]>(endpoint);
    }

    public postScore(data: IPostScoreAddress): Observable<IPostScoreAddressResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('scoreHistory'))
            .toString();
        return this.httpClient.post<IPostScoreAddressResponse>(endpoint, data);
    }

    public getAddressByZipCode(zipCode: string): Observable<IAddressByZipCode> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getAddressByZipCode'))
            .param('postalCode', zipCode)
            .toString();
        return this.httpClient.get<IAddressByZipCode>(endpoint);
    }

    private handleTierDowngraded(oldSubscriptionTier: number, newSubscriptionTier: number): void {
        forkJoin([
            this.getSubscriptionType(oldSubscriptionTier),
            this.getSubscriptionType(newSubscriptionTier),
        ]).subscribe(
            ([oldSubscriptionType, newSubscriptionType]) => {
                const downgradeData = {
                    oldHighVolumeProgramNumber: oldSubscriptionType.HighVolumeProgramNumber,
                    newHighVolumeProgramNumber: newSubscriptionType.HighVolumeProgramNumber,
                };

                this.showOfferDialog('downgrade', { downgrade: downgradeData });
            },
        );
    }

    private handleTierUpgraded(newSubscriptionTier: number): void {
        this.getSubscriptionType(newSubscriptionTier)
            .subscribe(
                (subscriptionType) => {
                    const upgradeData = {
                        shipmentCount: subscriptionType.ShipmentCount,
                        highVolumeProgramNumber: subscriptionType.HighVolumeProgramNumber,
                    };

                    this.showOfferDialog('upgrade', { upgrade: upgradeData });
                },
            );
    }

    public getAddressUpload(fileUploadId: string): Observable<Array<IGetAddressUploadInterface>> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('getAddressUpload'))
            .param('fileUploadId', fileUploadId)
            .toString();
        return this.httpClient.get<Array<IGetAddressUploadInterface>>(endpoint);
    }

    public getRecentUploads(fromDate?: string, toDate?: string): Observable<Array<IPostRecentUploadsInterface>> {
        let endpoint = "";
        
        if (fromDate && toDate) {
            endpoint = SafeUrlAssembler()
                .template(this.apiEndpointService.getEndpoint('getRecentUploads'))
                .param('From', fromDate)
                .param('To', toDate)
                .toString();
        } else {
            endpoint = SafeUrlAssembler()
                .template(this.apiEndpointService.getEndpoint('getRecentUploads'))
                .toString();
        }
        return this.httpClient.get<Array<IPostRecentUploadsInterface>>(endpoint);
    }

    public postAddressUpload(data: IPostAddressUpload): Observable<IPostAddressUploadResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('addressUpload'))
            .toString();
        return this.httpClient.post<IPostAddressUploadResponse>(endpoint, data);
    }

    public putAddressUpload(data: { CustomerId: string, BatchId: string }): Observable<string> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('addressUpload'))
            .toString();
        return this.httpClient.put<string>(endpoint, data);
    }

    public postAddressReview(data: IPostAddressReview): Observable<IPostAddressReviewResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('addressReview'))
            .toString();
        return this.httpClient.post<IPostAddressReviewResponse>(endpoint, data);
    }

    public postAddressUploadDraft(data: IPostAddressUpload): Observable<IPostAddressUploadDraftResponse> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('addressUploadDraft'))
            .toString();
        return this.httpClient.post<IPostAddressUploadDraftResponse>(endpoint, data);
    }

    public putAddressUploadDraft(data: IPutAddressUploadDraft): Observable<string> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('addressUploadDraft'))
            .toString();
        return this.httpClient.put<string>(endpoint, data);
    }

    public getAddressUploadDownload(fileUploadId: string): Observable<any> {
        const endpoint = SafeUrlAssembler()
            .template(this.apiEndpointService.getEndpoint('downloadAddressUpload'))
            .param('fileUploadId', fileUploadId)
            .toString();
        return this.httpClient.get<any>(endpoint);
    }
}
